import React from "react";
import ReactDOM from "react-dom/client";
import { store, persistor } from "./store/store";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import "./index.css";
import App from "./App";
import { StyledEngineProvider } from "@mui/material";
import { BrowserRouter } from "react-router-dom";
import { setCount } from "./store/slices/countSlice";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import ErrorBoundary from "./globals/components/ErrorBoundary";
import * as Sentry from "@sentry/react";
import { jwtDecode } from "jwt-decode";
import { scrubSentryEvent, scrubSentryBreadcrumb } from "./globals/utils/iamObservability";

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN || "",
  environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "production",
  tracesSampleRate: 0.1,
  // Strip tokenized URLs/queries the SDK auto-attaches (page URL, transaction
  // name, navigation/xhr breadcrumbs) — invite/reset tokens live in those paths.
  beforeSend: scrubSentryEvent,
  beforeBreadcrumb: scrubSentryBreadcrumb,
  // Session Replay — DVR-style front-end recording. Surfaces registration /
  // login friction that leaves no backend trace (e.g. "screen won't advance",
  // silent client-side failures). Records error sessions for playback in Sentry.
  // Per-env via REACT_APP_SENTRY_REPLAY_SESSION_RATE (default 0.1). Set to 1.0
  // on a given environment to record EVERY session — used during active
  // investigations to catch silent failures that throw no error (and so would
  // never trip replaysOnErrorSampleRate). Prod keeps the 0.1 default.
  replaysSessionSampleRate: process.env.REACT_APP_SENTRY_REPLAY_SESSION_RATE
    ? Number(process.env.REACT_APP_SENTRY_REPLAY_SESSION_RATE)
    : 0.1,
  replaysOnErrorSampleRate: 1.0, // capture 100% of sessions where an error fires
  // Never let Sentry auto-attach PII (IP address, request cookies/headers).
  // We attach a minimal user context explicitly (id + username only) on login.
  sendDefaultPii: false,
  integrations: [
    Sentry.replayIntegration({
      // PII safety (SOC 2 + Core Requirements): mask everything by default.
      // Registration forms carry names, emails, DOB, and password fields, so
      // every text node and input value is masked, and all media is blocked.
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
      // Network request/response BODIES are intentionally NOT captured (Sentry
      // default). Do not add networkDetailAllowUrls — keeps payloads out of replays.
    }),
  ],
});

// On app load (incl. page refresh / returning session), attach the user_id from
// the stored JWT so Sentry events are attributable to a user. username isn't in
// the token, so it's enriched on the next login (see handleLoginSuccess). When
// no/invalid token is present, the user context is cleared.
try {
  const token = localStorage.getItem("accessToken");
  if (token) {
    const { user_id } = jwtDecode(token);
    if (user_id != null) Sentry.setUser({ id: String(user_id) });
  } else {
    Sentry.setUser(null);
  }
} catch {
  Sentry.setUser(null);
}

const theme = createTheme({
  typography: {
    fontFamily: "'Roboto', sans-serif",
    allVariants: {
      fontFamily: "'Roboto', sans-serif",
    },
  },
});

// ✅ BroadcastChannel for syncing Redux state across tabs (with fallback)
try {
  const channel = new BroadcastChannel("redux_sync");
  channel.onmessage = (event) => {
    if (event.data && event.data.type === "UPDATE_COUNT") {
      store.dispatch(setCount(event.data.payload));
    }
  };
} catch (e) {
  // BroadcastChannel not available (incognito mode, iframe restrictions, etc.)
}

// Fallback: localStorage event listener for cross-tab sync
window.addEventListener("storage", (event) => {
  if (event.key === "count") {
    store.dispatch(setCount(Number(event.newValue)));
  }
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <Provider store={store}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <PersistGate loading={null} persistor={persistor}>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </PersistGate>
          </ThemeProvider>
        </StyledEngineProvider>
      </Provider>
    </ErrorBoundary>
  </React.StrictMode>
);
